import type { Editor } from '@tiptap/core';
import type { Transaction } from '@tiptap/pm/state';

type ImportImage = (url: string, signal: AbortSignal) => Promise<string>;

/** Import newly pasted network images without changing the selection or older images. */
export function watchNetworkImagePastes(editor: Editor, importImage: ImportImage,
    onStatus: (pending: number, failed: boolean) => void, onError?: (message: string) => void) {
    const controller = new AbortController();
    let pending = 0;
    let failed = false;
    let sequence = 0;
    const jobs: Array<() => Promise<void>> = [];
    let running = 0;
    const report = () => onStatus(pending, failed);
    const run = () => {
        while (!controller.signal.aborted && running < 3 && jobs.length) {
            running++;
            void jobs.shift()!().finally(() => { running--; run(); });
        }
    };
    const onTransaction = ({ transaction }: { transaction: Transaction }) => {
        if (!transaction.getMeta('paste') || !transaction.docChanged) return;
        if (pending === 0) failed = false;
        const previous = new Set<unknown>();
        transaction.before.descendants(node => { if (['image', 'inlineImage'].includes(node.type.name)) previous.add(node); });
        const images: Array<{ pos: number; src: string; url: string; id: string }> = [];
        transaction.doc.descendants((node, pos) => {
            if (!['image', 'inlineImage'].includes(node.type.name) || previous.has(node)) return;
            const src = node.attrs.src;
            if (typeof src !== 'string' || !/^(https?:)?\/\//i.test(src)) return;
            let url: string;
            try { url = new URL(src, editor.view.dom.ownerDocument.baseURI).href; }
            catch { failed = true; return; }
            images.push({ pos, src, url, id: `image-import-${Date.now()}-${++sequence}` });
        });
        if (!images.length) { if (failed) report(); return; }
        const tr = editor.state.tr;
        for (const image of images) {
            const node = tr.doc.nodeAt(image.pos)!;
            tr.setNodeMarkup(image.pos, undefined, { ...node.attrs, importId: image.id });
        }
        editor.view.dispatch(tr.setMeta('addToHistory', false));
        pending += images.length;
        report();
        const grouped = new Map<string, typeof images>();
        for (const image of images) grouped.set(image.src, [...(grouped.get(image.src) || []), image]);
        for (const [src, targets] of grouped) jobs.push(async () => {
            try {
                const dataUrl = await importImage(targets[0].url, controller.signal);
                if (controller.signal.aborted || editor.isDestroyed) return;
                const update = editor.state.tr;
                update.doc.descendants((node, pos) => {
                    if (['image', 'inlineImage'].includes(node.type.name) && node.attrs.src === src && targets.some(target => target.id === node.attrs.importId)) {
                        update.setNodeMarkup(pos, undefined, { ...node.attrs, src: dataUrl, importId: null });
                    }
                });
                if (update.docChanged) editor.view.dispatch(update.setMeta('addToHistory', false));
            } catch (error) {
                if (!controller.signal.aborted && !editor.isDestroyed) {
                    failed = true;
                    const failure = error as { response?: { status?: number; data?: { message?: unknown } }; message?: unknown } | null;
                    const detail = failure?.response?.data?.message || failure?.message;
                    const status = failure?.response?.status;
                    onError?.(typeof detail === 'string' ? detail.slice(0, 200)
                        : status ? `Image service returned HTTP ${status}.` : 'Image import failed.');
                }
            } finally {
                pending -= targets.length;
                if (!controller.signal.aborted && !editor.isDestroyed) report();
            }
        });
        run();
    };
    editor.on('transaction', onTransaction);
    return () => {
        controller.abort();
        jobs.length = 0;
        editor.off('transaction', onTransaction);
    };
}
